import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import carImage from '../Assets/gt3.png';
import rearWheelImage from '../Assets/rearWheel.png';
import './CarAnimation.css';

interface CarAnimationProps {
  visible: boolean;
  showEffects: boolean;
  participants: string[];
}

const CarAnimation: React.FC<CarAnimationProps> = ({ 
  visible, 
  showEffects,
  participants 
}) => {
  const [smokePuffs, setSmokePuffs] = useState<number[]>([]);
  const [flyingNames, setFlyingNames] = useState<{ id: number; name: string; angle: number }[]>([]);

  // Handle effects (smoke, names, wheel) when showEffects changes
  useEffect(() => {
    if (!showEffects) {
      setSmokePuffs([]);
      setFlyingNames([]);
      return;
    }

    // Start smoke effect
    const smokeInterval = setInterval(() => {
      setSmokePuffs((prev) => {
        const next = [...prev, Date.now()];
        return next.length > 100 ? next.slice(-100) : next;
      });
    }, 50);

    // Flying names effect
    const namesInterval = setInterval(() => {
      if (participants.length > 0) {
        const randomName = participants[Math.floor(Math.random() * participants.length)];
        const randomAngle = Math.random() * 360;
        setFlyingNames(prev => {
          const next = [...prev, { 
            id: Date.now(),
            name: randomName,
            angle: randomAngle
          }];
          return next.length > 25 ? next.slice(-25) : next;
        });
      }
    }, 150);

    return () => {
      clearInterval(smokeInterval);
      clearInterval(namesInterval);
    };
  }, [showEffects, participants]);

  return (
    <AnimatePresence>
      {visible && (
        <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              position: 'relative',
            }}
          >
            <div
              className="car-wrapper"
              style={{
                position: 'relative',
                width: '90%',
                maxWidth: '1536px',
                height: 'auto',
                zIndex: 100,
              }}
            >
              {/* Car container with effects */}
              <div style={{ position: 'relative', width: '100%', height: 'auto' }}>
                {/* Car image */}
                <motion.img
                  src={carImage}
                  alt="Porsche 911"
                  initial={{ x: '100vw' }}
                  animate={{ x: 0 }}
                  exit={{ x: '-100vw' }}
                  transition={{ duration: 2, ease: 'easeOut' }}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />

                {/* Effects */}
                {showEffects && (
                  <>
                    {/* Flying names */}
                    {flyingNames.map(({ id, name, angle }) => (
                      <motion.div
                        key={id}
                        className="flying-name"
                        initial={{ 
                          opacity: 1,
                          scale: 1.2,
                          left: '61.2%',
                          top: '48.5%',
                          rotate: angle
                        }}
                        animate={{ 
                          opacity: 0,
                          scale: 0.6,
                          left: `calc(61.2% + ${Math.cos(angle * Math.PI / 180) * 250}px)`,
                          top: `calc(48.5% + ${Math.sin(angle * Math.PI / 180) * 250}px)`,
                          rotate: angle + 540
                        }}
                        transition={{ 
                          duration: 1.2,
                          ease: 'easeOut'
                        }}
                        style={{
                          position: 'absolute',
                          color: '#DBE94A',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.6)',
                          pointerEvents: 'none',
                          zIndex: 106,
                          transform: 'translate(-50%, -50%)',
                        }}
                      >
                        {name}
                      </motion.div>
                    ))}

                    {/* Smoke puffs */}
                    {smokePuffs.map((id) => {
                      const randomX = Math.floor(Math.random() * 60 - 30);
                      const randomY = Math.floor(Math.random() * 80);
                      const randomScale = (1 + Math.random()).toFixed(2);
                      const randomRotate = Math.floor(Math.random() * 360);
                      const yRise = 200 + randomY;

                      // Check if device is mobile
            

                      return (
                        <div
                          className="smoke-puff"
                          key={id}
                          style={{
                            position: 'absolute',
                            left: '68.4%', // Adjust these values for mobile
                            top: '57.5%', // Adjust these values for mobile
                            transform: `translate(-50%, -50%) scale(${randomScale}) rotate(${randomRotate}deg)`,
                            // transformOrigin: 'center center',
                            '--x-drift': `${randomX}px`,
                            '--y-rise': `${yRise}px`,
                          } as React.CSSProperties}
                        />
                      );
                    })}

                    {/* Spinning wheel */}
                    <motion.img
                      src={rearWheelImage}
                      alt="Rear Wheel"
                      animate={{ rotate: -360 }}
                      transition={{ 
                        repeat: Infinity,
                        duration: 0.2,
                        ease: "linear"
                      }}
                      style={{
                        position: 'absolute',
                        left: '60.4%',
                        top: '48.5%',
                        width: '11.6%',
                        height: 'auto',
                        zIndex: 104,
                        transform: 'translate(-50%, -50%)',
                        transformOrigin: 'center center',
                        pointerEvents: 'none',
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CarAnimation;
